export const STUDY_ERROR_REASON_PARAM = 'studyErrorReason';

export type StudyNotFoundReason = 'permission-denied' | 'study-not-found' | 'request-failed';

export function isPermissionDeniedError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const candidate = error as {
    status?: number;
    statusCode?: number;
    response?: { status?: number };
    message?: string;
  };

  const status = candidate.status ?? candidate.statusCode ?? candidate.response?.status;
  if (status === 401 || status === 403) {
    return true;
  }

  const message = String(candidate.message || '').toLowerCase();
  return (
    message.includes('401') ||
    message.includes('403') ||
    message.includes('forbidden') ||
    message.includes('permission denied') ||
    message.includes('permission_denied') ||
    message.includes('not authorized') ||
    message.includes('unauthorized') ||
    message.includes('dicomwebread denied')
  );
}

export function parseStudyNotFoundReason(
  value: string | null | undefined
): StudyNotFoundReason | null {
  if (value === 'permission-denied' || value === 'study-not-found' || value === 'request-failed') {
    return value;
  }
  return null;
}

export function getStudyNotFoundMessage(
  reason: StudyNotFoundReason | null,
  storeName: string | null
): { title: string; detail?: string } {
  if (reason === 'permission-denied' && storeName) {
    return {
      title: `You don’t have permission to the DICOM store “${storeName}”.`,
      detail: 'Ask an administrator to grant access.',
    };
  }

  if (reason === 'permission-denied') {
    return {
      title: 'You don’t have permission to this DICOM store.',
      detail: 'Ask an administrator to grant access.',
    };
  }

  if (reason === 'study-not-found' && storeName) {
    return {
      title: `The requested study was not found in the DICOM store “${storeName}”.`,
      detail:
        'It may be in a different DICOM store, or the link may be incorrect. Return to the study list and try another study.',
    };
  }

  if (reason === 'study-not-found') {
    return {
      title: 'The requested study was not found in any DICOM store you can access.',
      detail:
        'The study may not exist, or your account may not have permission to view it. Return to the study list and try another study.',
    };
  }

  if (reason === 'request-failed' && storeName) {
    return {
      title: `Unable to load the requested study from the DICOM store “${storeName}”.`,
      detail: 'Check your network connection and try again.',
    };
  }

  return {
    title: 'One or more of the requested studies are not available at this time.',
  };
}
